import React from "react";
import { Accordion, AccordionSummary, AccordionDetails, Icon, Checkbox, FormControlLabel, Tooltip } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import ItemsTable from "../Items";
import { boxVolumetric, boxChargeBasis, vendorBoxWeight, capexBoxWeight } from "../../weights";

// Typing in an input inside the accordion header must not toggle the accordion.
const stop = (event) => event.stopPropagation();

/**
 * One box: editable dimensions/weight in the header, line items in the body.
 *
 * Props: rowData (box + number), updateBox(index, patch), deleteBox(index),
 *        duplicateBox(index), uomRequired
 */
export default function BoxRow(props) {
    const [expanded, setExpanded] = React.useState(false);
    const index = props.rowData.number - 1;
    const box = props.rowData;

    const patch = (fields) => props.updateBox(index, fields);
    const field = (label, key, width = 100) => (
        <MDInput
            label={label}
            value={box[key] ?? ""}
            type="number"
            inputProps={{ step: "any", min: 0 }}
            sx={{ width }}
            onClick={stop}
            onFocus={stop}
            onChange={(event) => patch({ [key]: event.target.value })}
        />
    );

    const vol = boxVolumetric(box);
    const basis = boxChargeBasis(box);

    return (
        <div style={{ display: "flex", alignItems: "center", margin: 10 }}>
            <div style={{ flex: 1 }}>
                <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, width: "100%", alignItems: 'center' }}>
                            <MDInput label={"Box"} value={box.number} disabled sx={{ width: 60 }} />
                            {field("Length (cm)", "length")}
                            {field("Breadth (cm)", "breadth")}
                            {field("Height (cm)", "width")}
                            {field("Weight (kg)", "weight")}
                            <MDInput label={"Items"} value={box.items ? box.items.length.toString() : "0"} disabled sx={{ width: 70 }} />
                            <MDInput label={"Volumetric"} value={vol ? vol.toFixed(2) : "0"} disabled sx={{ width: 100 }} />
                            <Tooltip title={`Charged on ${basis.toFixed(2)} kg (the higher of actual and volumetric)`}>
                                <span onClick={stop}>
                                    <MDInput label={"Vendor CW"} value={vendorBoxWeight(box).toString()} disabled sx={{ width: 95 }} />
                                </span>
                            </Tooltip>
                            <Tooltip title={`Charged on ${basis.toFixed(2)} kg (the higher of actual and volumetric)`}>
                                <span onClick={stop}>
                                    <MDInput label={"Capex CW"} value={capexBoxWeight(box).toString()} disabled sx={{ width: 95 }} />
                                </span>
                            </Tooltip>
                            <FormControlLabel
                                onClick={stop}
                                control={
                                    <Checkbox
                                        checked={!!box.contains_medicine}
                                        onClick={stop}
                                        onChange={(event) => patch({ contains_medicine: event.target.checked })}
                                    />
                                }
                                label="Contains Medicine"
                            />
                        </div>
                    </AccordionSummary>
                    <AccordionDetails>
                        <ItemsTable
                            updateItems={(items) => patch({ items })}
                            items={box.items || []}
                            containsMedicine={!!box.contains_medicine}
                            uomRequired={props.uomRequired}
                        />
                    </AccordionDetails>
                </Accordion>
            </div>
            <Tooltip title="Duplicate box (with its items)">
                <MDButton variant="gradient" color="info" iconOnly style={{ marginLeft: 10 }}
                    onClick={() => props.duplicateBox(index)}>
                    <Icon>content_copy</Icon>
                </MDButton>
            </Tooltip>
            <Tooltip title="Delete box">
                <MDButton variant="gradient" color="error" iconOnly style={{ margin: 10 }}
                    onClick={() => props.deleteBox(index)}>
                    <Icon>delete</Icon>
                </MDButton>
            </Tooltip>
        </div>
    );
}
